/**
 * Betreff-Schema ausgehender Mails (PO-Notiz 26.07.2026, Punkte 6+7).
 *
 * Wird eine Mail aus einem Vorgang heraus verfasst, ist der Betreff nach einem
 * festen Muster vorbelegt — editierbar, es ist ein Vorschlag:
 *
 *     [<Vorgangsnummer>] / Kunde - VSNR <Nr> / <Risikoobjekt> / <Betreff>
 *
 * Regeln, alle mit dem PO abgestimmt:
 *
 *  - Bei einem Ansprechpartner steht der FIRMENname im Betreff, nicht der des
 *    APs. (Konsistent zur Vorgangsnummer-Regel: dort gilt dasselbe.)
 *  - „VSNR" ist die Vertragsnummer, die auch in der Vertragsliste steht
 *    (`Contract.contractNbr`) — nicht `insuranceNbr`. Die Daten kennen beide.
 *  - Fehlt ein Segment, wird es WEGGELASSEN. Kein leeres „/ /", keine
 *    „___"-Platzhalter: ein Betreff geht an den Kunden raus, und Lücken darin
 *    sehen nach Vorlage aus, nicht nach Absicht.
 *  - Die VORGANGSNUMMER steht vorn in eckigen Klammern (PO 03.08.2026). Sie
 *    ersetzt den 26.07. entfernten technischen `[VG-<id>]`-Marker: dieselbe
 *    Nummer steht im Kopf des Vorgangs und wird am Telefon vorgelesen, ist für
 *    den Empfänger also nicht kryptisch. Die Verknüpfung hängt weiterhin NICHT
 *    daran — sie reist als `vorgangId` am Sendeauftrag mit.
 *
 * Bewusst DOM- und React-frei, damit die Regeln in Vitest prüfbar sind.
 */

export type MailSubjectParts = {
    /** Fachliche Vorgangsnummer — steht als „[<Nr>] / " ganz vorn. */
    vorgangNbr?: string | null;
    /** Kunde/Firma — bei Ansprechpartnern die Firma. */
    kunde?: string | null;
    /** Vertragsnummer (`contractNbr`), ohne das Wort „VSNR". */
    vsnr?: string | null;
    /** Name des Risikoobjekts. */
    risikoobjekt?: string | null;
    /** Freier Betreff-Teil, den der Nutzer meint. */
    betreff?: string | null;
};

const clean = (v: string | null | undefined): string => (v ?? "").replace(/\s+/g, " ").trim();

/**
 * Setzt den Betreff aus den vorhandenen Teilen zusammen.
 *
 * Der Kunde steht vorn und wird mit „ - " abgesetzt; alles Weitere hängt mit
 * „ / " an. Fehlende Teile fallen ersatzlos weg, auch der Kunde.
 *
 * Beispiele:
 *     { vorgangNbr: "10023-4", kunde: "Acme GmbH", vsnr: "4711", betreff: "Schaden" }
 *       → "[10023-4] / Acme GmbH - VSNR 4711 / Schaden"
 *     { kunde: "Acme GmbH", vsnr: "4711", risikoobjekt: "Halle 2", betreff: "Schaden" }
 *       → "Acme GmbH - VSNR 4711 / Halle 2 / Schaden"
 *     { kunde: "Acme GmbH", betreff: "Rückfrage" }
 *       → "Acme GmbH - Rückfrage"
 *     { betreff: "Rückfrage" }
 *       → "Rückfrage"
 */
export function buildMailSubject(parts: MailSubjectParts): string {
    const nummer = clean(parts.vorgangNbr);
    const kunde = clean(parts.kunde);
    const vsnr = clean(parts.vsnr);
    const risiko = clean(parts.risikoobjekt);
    const betreff = clean(parts.betreff);

    const tail = [vsnr ? `VSNR ${vsnr}` : "", risiko, betreff].filter(Boolean).join(" / ");

    const ohneNummer = !kunde ? tail : !tail ? kunde : `${kunde} - ${tail}`;
    if (!nummer) return ohneNummer;
    return ohneNummer ? `[${nummer}] / ${ohneNummer}` : `[${nummer}] / `;
}

/**
 * Vorbelegter Betreff plus Cursor-Position für das Compose-Fenster.
 *
 * Der Nutzer soll direkt weitertippen können, wo sein eigener Text hingehört —
 * hinter dem letzten Trenner. Ohne das müsste er sich erst ans Ende klicken.
 * Ist bereits ein `betreff` gesetzt, steht der Cursor hinter dem ganzen Text.
 */
export function buildMailSubjectPrefill(parts: MailSubjectParts): {
    subject: string;
    cursor: number;
} {
    const withoutBetreff = buildMailSubject({ ...parts, betreff: null });
    const full = buildMailSubject(parts);
    if (!withoutBetreff) return { subject: full, cursor: full.length };
    // Ohne eigenen Betreff-Teil endet der Vorschlag auf dem Präfix; damit der
    // Nutzer nicht selbst den Trenner tippen muss, hängt er hier schon dran.
    if (!clean(parts.betreff)) {
        const subject = `${withoutBetreff} / `;
        return { subject, cursor: subject.length };
    }
    return { subject: full, cursor: full.length };
}
